import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Heart,
    Shield,
    FileCheck,
    Car,
    Plane,
    Home,
    AlertTriangle,
    Activity,
    Briefcase,
    TrendingUp,
    PieChart,
    BarChart3,
    Layers,
    Clock,
    Crown,
    Users,
    Target,
    Wallet,
    GraduationCap,
    Calculator,
    Landmark,
    MessageSquare,
    UserCheck,
    Search,
    CheckCircle,
    ArrowUpRight,
    Phone,
    Sparkles,
} from "lucide-react";
import AnimatedSection, {
    StaggerContainer,
    StaggerItem,
} from "../../components/ui/AnimatedSection";

// Service Categories with comprehensive offerings
const serviceCategories = [
    {
        id: "insurance",
        category: "Insurance Solutions",
        categoryIcon: Shield,
        description: "Protect what matters most with comprehensive coverage for health, life, motor, travel, and business needs.",
        services: [
            {
                icon: Heart,
                title: "Health Insurance",
                description: "Comprehensive medical coverage with wide hospital networks and seamless cashless claims.",
                features: ["Cashless Hospitalization", "Pre & Post Care", "Day Care Procedures", "No Claim Bonus"],
            },
            {
                icon: Shield,
                title: "Life Insurance",
                description: "Long-term financial security ensuring protection and stability for your loved ones.",
                features: ["Death Benefit", "Maturity Benefit", "Tax Benefits", "Loan Facility"],
            },
            {
                icon: FileCheck,
                title: "Term Insurance",
                description: "High-value life coverage at affordable premiums for complete family protection.",
                features: ["High Sum Assured", "Affordable Premiums", "Rider Options", "Online Purchase"],
            },
            {
                icon: Car,
                title: "Motor Insurance",
                description: "Reliable coverage for cars and two-wheelers with fast claims and extensive network garages.",
                features: ["Own Damage Cover", "Third Party Liability", "Personal Accident", "Roadside Assistance"],
            },
            {
                icon: Plane,
                title: "Travel Insurance",
                description: "Global travel protection covering medical emergencies, delays, and unforeseen risks.",
                features: ["Medical Emergencies", "Trip Cancellation", "Lost Baggage", "Flight Delays"],
            },
            {
                icon: Home,
                title: "Home Insurance",
                description: "Complete protection for your home and belongings against natural and man-made risks.",
                features: ["Structure Cover", "Contents Cover", "Natural Disasters", "Theft Protection"],
            },
            {
                icon: AlertTriangle,
                title: "Personal Accident Insurance",
                description: "Financial support against accidental injuries, disability, and loss of income.",
                features: ["Accidental Death", "Permanent Disability", "Medical Expenses", "Weekly Compensation"],
            },
            {
                icon: Activity,
                title: "Critical Illness Insurance",
                description: "Lump-sum coverage for major illnesses to reduce financial stress during treatment.",
                features: ["Lump Sum Payout", "Multiple Illnesses", "Survival Period", "Add-on Option"],
            },
            {
                icon: Briefcase,
                title: "Commercial / Business Insurance",
                description: "Customized risk protection solutions for businesses, assets, and operations.",
                features: ["Property Insurance", "Liability Cover", "Business Interruption", "Employee Coverage"],
            },
        ],
    },
    {
        id: "investment",
        category: "Investment & Wealth Solutions",
        categoryIcon: TrendingUp,
        description: "Build lasting wealth through strategic investments in mutual funds, equities, ETFs, and managed portfolios.",
        services: [
            {
                icon: PieChart,
                title: "Mutual Fund Investments",
                description: "Goal-based investment solutions with diversified portfolios and professional fund management.",
                features: ["Diversified Portfolio", "Professional Management", "SIP Options", "Tax Benefits"],
            },
            {
                icon: BarChart3,
                title: "Equity (Stocks) Investment",
                description: "Direct stock investments focused on long-term growth and wealth creation.",
                features: ["Direct Equity", "Portfolio Building", "Research Support", "Regular Monitoring"],
            },
            {
                icon: Layers,
                title: "Exchange Traded Funds (ETF)",
                description: "Low-cost, transparent investment options tracking market indices and sectors.",
                features: ["Low Cost", "High Liquidity", "Index Tracking", "Diversification"],
            },
            {
                icon: Clock,
                title: "Systematic Investment Plans (SIP)",
                description: "Disciplined, periodic investments designed for steady long-term wealth building.",
                features: ["Rupee Cost Averaging", "Flexible Amounts", "Auto-Debit Facility", "Goal Tracking"],
            },
            {
                icon: Crown,
                title: "Portfolio Management Services (PMS)",
                description: "Professionally managed portfolios tailored to your risk appetite and financial goals.",
                features: ["Personalized Strategy", "Active Management", "Regular Reporting", "Tax Optimization"],
            },
        ],
    },
    {
        id: "retirement",
        category: "Retirement & Future Planning",
        categoryIcon: Users,
        description: "Secure your golden years with strategic retirement solutions and child education planning.",
        services: [
            {
                icon: Target,
                title: "Retirement Planning",
                description: "Strategic financial planning to ensure a stable and comfortable post-retirement life.",
                features: ["Corpus Calculation", "Investment Strategy", "Regular Income", "Inflation Protection"],
            },
            {
                icon: Wallet,
                title: "Pension & Annuity Plans",
                description: "Guaranteed income solutions for financial independence after retirement.",
                features: ["Guaranteed Income", "Life Coverage", "Flexible Payout", "Tax Benefits"],
            },
            {
                icon: GraduationCap,
                title: "Child Education Planning",
                description: "Structured savings and investment plans to secure your child's future education.",
                features: ["Goal-Based Saving", "Education Corpus", "Insurance Cover", "Flexible Options"],
            },
        ],
    },
    {
        id: "savings",
        category: "Savings & Tax Planning",
        categoryIcon: Calculator,
        description: "Maximize your savings with tax-efficient investment strategies and fixed income options.",
        services: [
            {
                icon: Calculator,
                title: "Tax-Saving Investments",
                description: "Smart investment options designed to reduce tax liability while growing wealth.",
                features: ["Section 80C Benefits", "ELSS Funds", "PPF & NPS", "Tax-Free Returns"],
            },
            {
                icon: Landmark,
                title: "Fixed Income Solutions",
                description: "Stable and predictable returns through secure, low-risk investment instruments.",
                features: ["Fixed Deposits", "Bonds & Debentures", "Government Securities", "Guaranteed Returns"],
            },
        ],
    },
    {
        id: "advisory",
        category: "Advisory & Support Services",
        categoryIcon: MessageSquare,
        description: "Expert guidance for financial planning, risk assessment, and comprehensive wealth management.",
        services: [
            {
                icon: UserCheck,
                title: "Financial Planning Advisory",
                description: "Personalized financial guidance aligned with your life goals and risk profile.",
                features: ["Goal Assessment", "Risk Profiling", "Investment Planning", "Regular Review"],
            },
            {
                icon: Search,
                title: "Risk Assessment & Insurance Advisory",
                description: "Expert evaluation to identify risks and recommend the right protection strategies.",
                features: ["Risk Analysis", "Coverage Assessment", "Policy Review", "Claims Support"],
            },
            {
                icon: Crown,
                title: "Wealth Management Services",
                description: "End-to-end wealth solutions focused on growth, protection, and preservation.",
                features: ["Holistic Approach", "Estate Planning", "Succession Planning", "Legacy Building"],
            },
        ],
    },
];

export default function ServicesPage() {
    return (
        <div className="overflow-hidden">
            {/* Hero Section */}
            <section className="relative py-32 bg-cream">
                <div className="absolute inset-0 bg-pattern" />
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#d4b86a]/5 to-transparent" />

                <div className="container-custom px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <span className="badge mb-4">
                            <Sparkles className="w-3 h-3 mr-2" />
                            Our Services
                        </span>
                        <h1 className="text-gray-900 mb-6">
                            Comprehensive
                            <span className="block mt-1 gradient-text">
                                Financial Services
                            </span>
                        </h1>
                        <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                            From insurance protection to wealth management, we
                            offer tailored solutions designed for every stage of your
                            financial journey. Explore our complete range of services below.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Quick Navigation */}
            <section className="py-6 bg-white border-b border-gray-100 sticky top-0 z-40">
                <div className="container-custom">
                    <div className="flex flex-wrap gap-3 justify-center">
                        {serviceCategories.map((cat) => (
                            <a
                                key={cat.id}
                                href={`#${cat.id}`}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-gray-600 hover:text-[#c9a84c] hover:bg-[#c9a84c]/10 transition-all"
                            >
                                <cat.categoryIcon className="w-4 h-4" />
                                {cat.category}
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* Service Categories */}
            {serviceCategories.map((category, categoryIndex) => (
                <section
                    key={category.id}
                    id={category.id}
                    className={`section-padding ${categoryIndex % 2 === 0 ? 'bg-white' : 'bg-offwhite'}`}
                >
                    <div className="container-custom">
                        {/* Category Header */}
                        <AnimatedSection className="text-center mb-16">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#c9a84c]/10 to-[#d4b86a]/20 mb-6">
                                <category.categoryIcon className="w-8 h-8 text-[#c9a84c]" />
                            </div>
                            <h2 className="text-gray-900 mb-4">{category.category}</h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                {category.description}
                            </p>
                        </AnimatedSection>

                        {/* Services Grid */}
                        <StaggerContainer
                            className={`grid gap-6 ${category.services.length <= 2
                                ? 'md:grid-cols-2 lg:grid-cols-2 max-w-4xl mx-auto'
                                : 'md:grid-cols-2 lg:grid-cols-3'
                                }`}
                        >
                            {category.services.map((service) => (
                                <StaggerItem key={service.title}>
                                    <motion.article
                                        whileHover={{ y: -6 }}
                                        className="bg-white rounded-xl p-6 h-full flex flex-col border border-gray-100 hover:border-[#d4b86a]/40 hover:shadow-lg transition-all duration-300"
                                    >
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#c9a84c]/10 to-[#d4b86a]/20 flex items-center justify-center mb-5">
                                            <service.icon className="w-6 h-6 text-[#c9a84c]" />
                                        </div>

                                        <h3 className="text-lg font-display text-gray-900 mb-2">
                                            {service.title}
                                        </h3>

                                        <p className="text-gray-600 text-sm leading-relaxed mb-5 flex-grow">
                                            {service.description}
                                        </p>

                                        <div className="space-y-2 pt-4 border-t border-gray-100">
                                            {service.features.map((feature) => (
                                                <div
                                                    key={feature}
                                                    className="flex items-center space-x-2"
                                                >
                                                    <CheckCircle className="w-3.5 h-3.5 text-[#c9a84c] flex-shrink-0" />
                                                    <span className="text-xs text-gray-500">
                                                        {feature}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.article>
                                </StaggerItem>
                            ))}
                        </StaggerContainer>
                    </div>
                </section>
            ))}

            {/* Process Section */}
            <section className="section-padding bg-white relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4b86a]/30 to-transparent" />

                <div className="container-custom relative z-10">
                    <AnimatedSection className="text-center mb-16">
                        <span className="badge mb-4">Our Process</span>
                        <h2 className="text-gray-900 mb-2">How We Work</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            A refined four-step approach to securing your
                            financial future
                        </p>
                    </AnimatedSection>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                step: "01",
                                title: "Consultation",
                                desc: "Complimentary consultation to understand your needs and goals",
                            },
                            {
                                step: "02",
                                title: "Analysis",
                                desc: "Comprehensive analysis of your current financial situation",
                            },
                            {
                                step: "03",
                                title: "Strategy",
                                desc: "Custom strategy tailored to your objectives and risk profile",
                            },
                            {
                                step: "04",
                                title: "Implementation",
                                desc: "Seamless execution with ongoing support and monitoring",
                            },
                        ].map((item, index) => (
                            <motion.div
                                key={item.step}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="text-center group"
                            >
                                <div className="relative inline-block mb-4">
                                    <span className="text-7xl font-display font-bold text-[#d4b86a]/10 group-hover:text-[#d4b86a]/20 transition-colors">
                                        {item.step}
                                    </span>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-12 h-12 border-2 border-[#d4b86a]/30 rounded-full flex items-center justify-center group-hover:bg-[#d4b86a]/10 transition-colors">
                                            <span className="text-[#c9a84c] font-display font-semibold">
                                                {item.step}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <h3 className="text-base font-display text-gray-900 mb-1">
                                    {item.title}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    {item.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 section-dark">
                <div className="container-custom px-6 relative z-10">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                        <AnimatedSection>
                            <h2 className="text-white mb-2">
                                Ready to Get Started?
                            </h2>
                            <p className="text-lg text-gray-300">
                                Speak with our experts for a complimentary
                                consultation
                            </p>
                        </AnimatedSection>

                        <AnimatedSection
                            delay={0.2}
                            className="flex flex-wrap gap-4"
                        >
                            <Link to="/contact">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="btn-primary inline-flex items-center space-x-2"
                                >
                                    <span>Book Consultation</span>
                                    <ArrowUpRight className="w-4 h-4" />
                                </motion.button>
                            </Link>
                            <motion.a
                                href="tel:+919876543210"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="btn-outline inline-flex items-center space-x-2 !border-white/30 !text-white hover:!bg-white hover:!text-gray-900"
                            >
                                <Phone className="w-4 h-4" />
                                <span>Call Now</span>
                            </motion.a>
                        </AnimatedSection>
                    </div>
                </div>
            </section>
        </div>
    );
}
